import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

/**
 * Wraps request with loading and data
 *
 * Example:
 * ```js
 * const { data: bibleStudies } = useAsyncRequest(
 *  useCallback(() => request<BibleStudy[]>(apiConfigs.getBibleStudies(defaultChurchId)), [request])
 * );
 * ```
 *
 * @param runRequest wrapped in useCallback hook
 * @param runCallback wrapped in useCallback hook
 * @param runErrorCallback wrapped in useCallback hook
 */
export const useAsyncRequest = <T>(
  runRequest: () => Promise<T>,
  runCallback?: <Response>(response: Response) => void,
  runErrorCallback?: (error: any) => void
) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<T | null>(null);

  // To prevent updates on unmount
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      runRequest()
        .then(response => {
          if (isMounted.current) {
            setData(response);
            setLoading(false);
            runCallback?.(response);
          }
        })
        .catch(error => {
          if (isMounted.current) {
            setLoading(false);
            if (runErrorCallback) {
              runErrorCallback(error);
            } else {
              toast.error(error.message || 'Something went wrong!');
            }
          }
        });
    })();
  }, [runCallback, runErrorCallback, runRequest]);

  return {
    loading,
    data,
  };
};
